import React, { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Alert, Box, Link, Tab, Tabs, Typography } from '@mui/material'
import { Page, Preloader } from '@vedmarket/ui-kit'

import { APPLICATION_LIST_TAB, COLORS, ROUTES } from '../../../config/constants'
import { useStaffApplications } from '../../../api/auth'
import {
  useListAdminApplicationsQuery,
  useListApplicationsQuery,
  useMetaStatusesQuery,
} from '../../../api/applications'
import { useApplicationsList, useApplicationStatuses } from '../../../api/applications'
import ApplicationsFilters from './filters'
import ApplicationsTable from './table'

import './applications.scss'
import './applications-page.scss'

const DEFAULT_LIMIT = 10

const Applications = () => {
  const { t, i18n } = useTranslation()

  const canManageApplications = useStaffApplications()
  const [ tab, setTab ] = useState<APPLICATION_LIST_TAB>(APPLICATION_LIST_TAB.MINE)
  const [ params, setParams ] = useState<ApplicationListState>({ page: 1, limit: DEFAULT_LIMIT, ordering: '-created_at' })
  const [ tableVersion, setTableVersion ] = useState(0)

  const isQueue = canManageApplications && tab === APPLICATION_LIST_TAB.QUEUE

  const listParams = useMemo(() => ({
    page: params.page,
    limit: params.limit,
    ordering: params.ordering,
    ...(params.status ? { status: params.status } : {}),
    ...(params.search ? { search: params.search } : {}),
  }), [ params ])

  const mineQuery = useListApplicationsQuery(listParams, { skip: isQueue, refetchOnMountOrArgChange: true })
  const queueQuery = useListAdminApplicationsQuery(listParams, { skip: !isQueue, refetchOnMountOrArgChange: true })
  const { isError: statusesError } = useMetaStatusesQuery()
  const statuses = useApplicationStatuses()
  const data = useApplicationsList({ params: listParams, managerView: isQueue })

  const active = isQueue ? queueQuery : mineQuery
  const { currentData, isFetching, isError, isSuccess } = active

  const hasStatusFilter = Boolean(params.status)
  const hasSearch = Boolean(params.search)
  const hasCriteria = hasStatusFilter || hasSearch
  const isEmptyList = (data?.count ?? 0) === 0
  const hasNoApplications = isEmptyList && !hasCriteria
  const hasSuccessfulList = Boolean(data) && isSuccess
  const hasListError = isError || statusesError
  const canShowListResult = hasSuccessfulList && !hasListError
  const showEmptyState = hasNoApplications && canShowListResult
  const hasNoResults = isEmptyList && canShowListResult
  const hasLoadedList = Boolean(mineQuery.data || queueQuery.data)
  const isTableLoading = isFetching && !hasLoadedList

  useEffect(() => {
    const hasNextPage = params.page > 1
    const hasEmptyPage = currentData?.results.length === 0
    const canRecoverPage = isSuccess && !isFetching
    const shouldRecoverPage = hasNextPage && hasEmptyPage
    if (canRecoverPage && shouldRecoverPage) {
      setParams((current) => ({ ...current, page: 1 }))
      setTableVersion((current) => current + 1)
    }
  }, [ currentData, params.page, isFetching, isSuccess ])

  const handleTableRequest = ({ page, limit, ordering }: ApplicationTableRequest) => {
    const hasChangedOrdering = ordering !== params.ordering
    const nextPage = hasChangedOrdering ? 1 : page

    setParams((current) => ({ ...current, page: nextPage, limit, ordering }))

    if (hasChangedOrdering) {
      setTableVersion((current) => current + 1)
    }
  }

  const handleFiltersApply = (status: string) => {
    setParams((current) => ({
      ...current,
      page: 1,
      status: status || undefined,
    }))
    setTableVersion((current) => current + 1)
  }

  const handleSearch = (search: string) => {
    setParams((current) => ({
      ...current,
      page: 1,
      search: search.trim() || undefined,
    }))
    setTableVersion((current) => current + 1)
  }

  const header = (
    <header className='applications-page__header'>
      <Link
        href={`/${ROUTES.CABINET}`}
        underline='hover'
        sx={{ cursor: 'pointer', color: COLORS.LIGHT_TEXT_SECONDARY }}
      >
        <Typography
          variant='body2'
          sx={{ color: COLORS.LIGHT_TEXT_SECONDARY, lineHeight: '21px', letterSpacing: 0 }}
        >
          {t('menu__text__main')}
        </Typography>
      </Link>
      <div className='applications-page__title'>
        <Typography
          variant='h4'
          component='div'
          sx={{ fontSize: 32, fontWeight: 700, lineHeight: '40px', letterSpacing: 0 }}
        >
          {t('applications__text__title')}
        </Typography>
      </div>
      {canManageApplications && (
        <Tabs
          className='applications-page__tabs'
          value={tab}
          onChange={(_, value: APPLICATION_LIST_TAB) => {
            setTab(value)
            setParams((current) => ({ ...current, page: 1 }))
            setTableVersion((current) => current + 1)
          }}
        >
          <Tab value={APPLICATION_LIST_TAB.MINE} label={<Typography variant='inherit'>{t('applications__text__tabMine')}</Typography>} />
          <Tab value={APPLICATION_LIST_TAB.QUEUE} label={<Typography variant='inherit'>{t('applications__text__tabQueue')}</Typography>} />
        </Tabs>
      )}
    </header>
  )

  const table = hasNoResults
    ? <Typography variant='body1'>{t('applications__text__noResults')}</Typography>
    : (
      <ApplicationsTable
        key={`${tableVersion}-${isQueue ? 'queue' : 'mine'}`}
        rows={data?.results ?? []}
        count={data?.count ?? 0}
        limit={params.limit}
        ordering={params.ordering}
        managerQueue={isQueue}
        onRequest={handleTableRequest}
      />
    )

  const tableContent = (
    <div aria-busy={isFetching}>
      {isFetching && <Preloader />}
      <div hidden={isFetching}>{table}</div>
    </div>
  )

  const statusOptions = statuses ?? []
  const filters = statusOptions.length > 0
    ? <ApplicationsFilters options={statusOptions} appliedStatus={params.status} onApply={handleFiltersApply} />
    : undefined

  return (
    <Box
      component='main'
      className='applications-page'
      lang={i18n.language}
    >
      {(isError || statusesError) && (
        <Alert severity='error' sx={{ marginBottom: 2 }}>
          <Typography variant='body1'>{t('common__text__error')}</Typography>
        </Alert>
      )}
      {showEmptyState ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
          {header}
          <Typography
            variant='body1'
            sx={{ color: COLORS.BLACK_DARK, lineHeight: '24px', letterSpacing: 0 }}
          >
            {t('applications__text__empty')}
          </Typography>
        </Box>
      ) : (
        <Page
          isSelected={hasStatusFilter}
          header={header}
          onSearch={handleSearch}
          isLoading={isTableLoading}
          table={tableContent}
          filters={filters}
        />
      )}
    </Box>
  )
}

export default Applications
